import React, { useState } from 'react'
import { Modal, Form, Button } from 'react-bootstrap'
import { Model } from '../../types/models/model'
import { Search, Brain, Check, Calendar } from 'lucide-react'
import styled from 'styled-components'
import { FaSpinner, FaCheck, FaExclamationTriangle } from 'react-icons/fa'
import { Project } from '../../types/models/project'

const StyledModal = styled(Modal)`
  .modal-content {
    background: #1a1a1a;
    border: none;
  }

  .modal-header {
    background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
    border-bottom: none;
    padding: 1.5rem 2rem;

    .modal-title {
      color: white;
      font-weight: 700;
      font-size: 1.5rem;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    }
  }

  .modal-body {
    background: #1a1a1a;
    padding: 2rem;
    max-height: 70vh;
    overflow-y: auto;
  }
`

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 2rem;

  .search-icon {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #b0b0b0;
  }

  .form-control {
    padding-left: 2.75rem;
    height: 46px;
    background: #2a2a2a;
    border: 2px solid #404040;
    border-radius: 12px;
    color: #ffffff;
    font-size: 1rem;

    &:focus {
      background: #333333;
      border-color: #34d399;
      box-shadow: 0 0 0 3px rgba(52, 211, 153, 0.2);
      color: #ffffff;
    }

    &::placeholder {
      color: #808080;
    }
  }
`

const IterationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const IterationCard = styled.div<{ $selected: boolean }>`
  background: ${(props) => (props.$selected ? 'rgba(52, 211, 153, 0.1)' : '#2a2a2a')};
  border: 2px solid ${(props) => (props.$selected ? '#34d399' : '#404040')};
  border-radius: 12px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    border-color: #34d399;
    background: rgba(52, 211, 153, 0.05);
    transform: translateY(-1px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
  }

  ${(props) =>
    props.$selected &&
    `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 4px;
      height: 100%;
      background: #34d399;
      border-top-left-radius: 12px;
      border-bottom-left-radius: 12px;
    }
  `}
`

const IterationHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`

const IterationInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
`

const IterationIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.4);
  flex-shrink: 0;
`

const IterationDetails = styled.div`
  flex: 1;
  min-width: 0;
`

const IterationName = styled.h4`
  font-size: 1.1rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 0.5rem 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const IterationMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`

const StatusBadge = styled.div<{ $status: string }>`
  background: ${(props) => {
    switch (props.$status.toLowerCase()) {
      case 'trained':
        return 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
      case 'training':
        return 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
      case 'failed':
        return 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
      default:
        return 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)'
    }
  }};
  color: white;
  font-weight: 600;
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const IterationNumber = styled.span`
  color: #34d399;
  font-weight: 600;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const CreatedDate = styled.span`
  color: #b0b0b0;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`

const SelectedIndicator = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.4);
  z-index: 3;
`

const MetricsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #404040;
`

const MetricItem = styled.div`
  text-align: center;

  .metric-value {
    font-size: 1.1rem;
    font-weight: 700;
    color: #ffffff;
    margin-bottom: 0.25rem;
  }

  .metric-label {
    font-size: 0.75rem;
    color: #b0b0b0;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #b0b0b0;

  .empty-icon {
    width: 80px;
    height: 80px;
    margin: 0 auto 1.5rem;
    opacity: 0.3;
  }

  .empty-title {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
    color: #ffffff;
  }

  .empty-subtitle {
    font-size: 1.1rem;
    max-width: 500px;
    margin: 0 auto;
    line-height: 1.5;
  }
`

const ActionBar = styled.div`
  background: #2a2a2a;
  padding: 1.5rem 2rem;
  border-top: 1px solid #404040;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.3);
  position: sticky;
  bottom: 0;
  z-index: 10;
  margin: 0 -2rem -2rem -2rem;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
`

const SelectionInfo = styled.div`
  background: linear-gradient(135deg, rgba(52, 211, 153, 0.2) 0%, rgba(16, 185, 129, 0.2) 100%);
  border: 2px solid rgba(52, 211, 153, 0.6);
  color: #34d399;
  border-radius: 16px;
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  flex: 1;
  margin-right: 1rem;

  .selection-text {
    font-weight: 600;
    font-size: 1.1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`

interface SelectIterationModalProps {
  show: boolean
  onHide: () => void
  onIterationSelect: (model: Model) => void
  childModels: Model[]
  selectedIterationId?: string | null
  baseModelName: string
  project?: Project
}

const SelectIterationModal: React.FC<SelectIterationModalProps> = ({
  show,
  onHide,
  onIterationSelect,
  childModels,
  selectedIterationId,
  baseModelName,
  project,
}) => {
  const [search, setSearch] = useState('')
  const [selectedModel, setSelectedModel] = useState<Model | null>(
    childModels.find((m) => m.id === selectedIterationId) || null
  )

  // Get model status
  const getModelStatus = (model: Model) => {
    if (model.state === 'TRAINING') return 'training'
    if (model.state === 'TRAINED') return 'trained'
    if (model.state === 'ERR') return 'failed'
    return 'unknown'
  }

  // Calculate key metrics for display
  const getKeyMetrics = (model: Model) => {
    const isClassification = project?.annotation_type === 'classification'

    if (isClassification) {
      const valAcc = model.metrics?.['validation:accuracy']
      const trainAcc = model.metrics?.['train:accuracy']

      return [
        {
          label: 'Val Acc',
          value: typeof valAcc === 'number' ? `${(valAcc * 100).toFixed(1)}%` : 'N/A',
        },
        {
          label: 'Train Acc',
          value: typeof trainAcc === 'number' ? `${(trainAcc * 100).toFixed(1)}%` : 'N/A',
        },
      ]
    } else {
      // Bounding box metrics
      const mAP = model.metrics?.['validation:mAP50']
      const precision = model.metrics?.['validation:precision']
      const recall = model.metrics?.['validation:recall']

      let f1Value = 'N/A'
      if (
        typeof precision === 'number' &&
        typeof recall === 'number' &&
        precision > 0 &&
        recall > 0
      ) {
        const f1 = (2 * (precision * recall)) / (precision + recall)
        f1Value = `${(f1 * 100).toFixed(1)}%`
      }

      return [
        {
          label: 'mAP@50',
          value: typeof mAP === 'number' ? `${(mAP * 100).toFixed(1)}%` : 'N/A',
        },
        {
          label: 'F1 Score',
          value: f1Value,
        },
        {
          label: 'Precision',
          value: typeof precision === 'number' ? `${(precision * 100).toFixed(1)}%` : 'N/A',
        },
        {
          label: 'Recall',
          value: typeof recall === 'number' ? `${(recall * 100).toFixed(1)}%` : 'N/A',
        },
      ]
    }
  }

  // Get iteration number (child models sorted by date, so index + 1)
  const getIterationNumber = (model: Model) => {
    const sortedByDate = [...childModels].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    )
    const index = sortedByDate.findIndex((m) => m.id === model.id)
    return index >= 0 ? index + 1 : 0
  }

  // Filter models based on search
  const filteredModels = childModels.filter((model) =>
    model.name.toLowerCase().includes(search.toLowerCase())
  )

  const handleModelSelect = (model: Model) => {
    setSelectedModel(model)
  }

  const handleConfirmSelection = () => {
    if (selectedModel) {
      onIterationSelect(selectedModel)
      onHide()
    }
  }

  return (
    <StyledModal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title className="d-flex align-items-center gap-2">
          <Brain size={24} />
          Select Fine-Tuned Iteration
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
          <h3
            style={{
              color: '#ffffff',
              fontSize: '1.25rem',
              fontWeight: 600,
              margin: '0 0 0.5rem 0',
            }}
          >
            Compare against "{baseModelName}"
          </h3>
          <p style={{ color: '#b0b0b0', fontSize: '0.95rem', margin: 0 }}>
            Choose a fine-tuned iteration to compare with your base model
          </p>
        </div>

        <SearchContainer>
          <Search size={18} className="search-icon" />
          <Form.Control
            type="text"
            placeholder="Search iterations by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </SearchContainer>

        {filteredModels.length === 0 ? (
          <EmptyState>
            <Brain className="empty-icon" />
            <h3 className="empty-title">
              {childModels.length === 0 ? 'No Iterations Yet' : 'No Matching Iterations'}
            </h3>
            <p className="empty-subtitle">
              {childModels.length === 0
                ? 'Create fine-tuned iterations by training your base model with new data to see comparison options.'
                : 'No iterations match your search criteria. Try adjusting your search terms.'}
            </p>
          </EmptyState>
        ) : (
          <IterationsList>
            {filteredModels.map((model) => {
              const isSelected = selectedModel?.id === model.id
              const keyMetrics = getKeyMetrics(model)
              const iterationNumber = getIterationNumber(model)
              const status = getModelStatus(model)

              return (
                <IterationCard
                  key={model.id}
                  $selected={isSelected}
                  onClick={() => handleModelSelect(model)}
                >
                  {isSelected && (
                    <SelectedIndicator>
                      <Check size={18} />
                    </SelectedIndicator>
                  )}

                  <IterationHeader>
                    <IterationInfo>
                      <IterationIcon>
                        <Brain size={24} />
                      </IterationIcon>
                      <IterationDetails>
                        <IterationName>{model.name}</IterationName>
                        <IterationMeta>
                          <StatusBadge $status={status}>
                            {status === 'training' && (
                              <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                            )}
                            {status === 'trained' && <FaCheck />}
                            {status === 'failed' && <FaExclamationTriangle />}
                            {status}
                          </StatusBadge>
                          <IterationNumber>Iteration #{iterationNumber}</IterationNumber>
                          <CreatedDate>
                            <Calendar size={12} />
                            {new Date(model.created_at).toLocaleDateString()}
                          </CreatedDate>
                        </IterationMeta>
                      </IterationDetails>
                    </IterationInfo>
                  </IterationHeader>

                  <MetricsRow>
                    {keyMetrics.slice(0, 4).map((metric, index) => (
                      <MetricItem key={index}>
                        <div className="metric-value">{metric.value}</div>
                        <div className="metric-label">{metric.label}</div>
                      </MetricItem>
                    ))}
                  </MetricsRow>
                </IterationCard>
              )
            })}
          </IterationsList>
        )}
      </Modal.Body>

      <ActionBar>
        {selectedModel ? (
          <SelectionInfo>
            <div className="selection-text">
              <Check size={18} />
              <strong>{selectedModel.name}</strong> selected for comparison
            </div>
          </SelectionInfo>
        ) : (
          <div style={{ color: '#b0b0b0', fontWeight: 500 }}>
            <Brain size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
            No iteration selected
          </div>
        )}

        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmSelection}
            disabled={!selectedModel}
            style={{
              background: selectedModel
                ? 'linear-gradient(135deg, #34d399 0%, #10b981 100%)'
                : undefined,
              border: 'none',
            }}
          >
            Compare Models
          </Button>
        </div>
      </ActionBar>
    </StyledModal>
  )
}

export default SelectIterationModal
